<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { getContrastingTextColor } from '@chatwoot/utils';
import CustomButton from 'shared/components/Button.vue';
import CsWidgetChatEditor from 'widget/v2/components/CsWidgetChatEditor.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { sendEmailTranscript } from 'widget/api/conversation';
import { useRouter } from 'widget/composables/route';
import { useStore, mapGetter } from 'widget/composables/store';
import { useI18n } from 'widget/composables/useI18n';
import { IFrameHelper } from 'widget/helpers/utils';
import { CHATWOOT_ON_START_CONVERSATION } from 'widget/constants/sdkEvents';

defineProps({
  msg: {
    type: String,
    default: '',
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// Refs
const chatInputComponent = ref(null);
const inReplyTo = ref(null);

// Getters
const conversationAttributes = mapGetter(
  'conversationAttributes/getConversationParams'
);
const widgetColor = mapGetter('appConfig/getWidgetColor');
const conversationSize = mapGetter('conversation/getConversationSize');
const currentUser = mapGetter('contacts/getCurrentUser');
const isWidgetStyleFlat = mapGetter('appConfig/isWidgetStyleFlat');

// Computed
const textColor = computed(() => getContrastingTextColor(widgetColor.value));

const hideReplyBox = computed(() => {
  const { allowMessagesAfterResolved } = window.chatwootWebChannel;
  const { status } = conversationAttributes.value;
  return !allowMessagesAfterResolved && status === 'resolved';
});

const showEmailTranscriptButton = computed(
  () => currentUser.value && currentUser.value.email
);

// Methods
const handleSendMessage = async content => {
  await store.dispatch('conversation/sendMessage', {
    content,
    replyTo: inReplyTo.value ? inReplyTo.value.id : null,
  });
  inReplyTo.value = null;
  if (conversationSize.value === 0) {
    store.dispatch('conversationAttributes/getAttributes');
  }
};

const handleSendAttachment = async attachment => {
  await store.dispatch('conversation/sendAttachment', {
    attachment,
    replyTo: inReplyTo.value ? inReplyTo.value.id : null,
  });
  inReplyTo.value = null;
};

const startNewConversation = () => {
  store.dispatch('conversation/clearConversations');
  store.dispatch('conversationAttributes/clearConversationAttributes');
  router.replace({ name: 'prechat-form' });
  IFrameHelper.sendMessage({
    event: 'onEvent',
    eventIdentifier: CHATWOOT_ON_START_CONVERSATION,
    data: { hasConversation: true },
  });
};

const toggleReplyTo = message => {
  inReplyTo.value = message;
  nextTick(() => {
    if (chatInputComponent.value) {
      chatInputComponent.value.focusInput();
    }
  });
};

const sendTranscript = async () => {
  const { email } = currentUser.value;
  if (email) {
    try {
      await sendEmailTranscript({ email });
      window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
        message: t('EMAIL_TRANSCRIPT.SEND_EMAIL_SUCCESS'),
        type: 'success',
      });
    } catch (error) {
      window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
        message: t('EMAIL_TRANSCRIPT.SEND_EMAIL_ERROR'),
      });
    }
  }
};

onMounted(() => {
  bus.$on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, toggleReplyTo);
});
</script>

<template>
  <footer
    v-if="!hideReplyBox"
    data-component="ChatFooter"
    data-version="2"
    class="relative z-50 mb-1 px-3"
    :class="{ 'rounded-lg': !isWidgetStyleFlat }"
  >
    <CsWidgetChatEditor
      ref="chatInputComponent"
      class="shadow-sm"
      :in-reply-to="inReplyTo"
      :on-send-message="handleSendMessage"
      :on-send-attachment="handleSendAttachment"
      @click-dismiss-reply-to="inReplyTo = null"
    />
  </footer>
  <div v-else>
    <CustomButton
      class="font-medium"
      block
      :bg-color="widgetColor"
      :text-color="textColor"
      @click="startNewConversation"
    >
      {{ $t('START_NEW_CONVERSATION') }}
    </CustomButton>
    <CustomButton
      v-if="showEmailTranscriptButton"
      type="clear"
      class="font-normal"
      @click="sendTranscript"
    >
      {{ $t('EMAIL_TRANSCRIPT.BUTTON_TEXT') }}
    </CustomButton>
  </div>
</template>
